import React, { useState, useEffect, useRef } from 'react';
import { parseMetadata } from '../utils/metadataParser';

const SidePanel = ({ pin, onNavigate }) => {
  const [content, setContent] = useState({
    title: '',
    firstParagraph: '',
    firstImage: null,
    category: '',
    date: '',
    loading: true,
    error: false
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousPin, setPreviousPin] = useState(null);
  const descriptionRef = useRef(null);

  useEffect(() => {
    if (!pin) {
      setContent({
        title: '',
        firstParagraph: '',
        firstImage: null,
        loading: false,
        error: false
      });
      return;
    }

    // If this is a new pin (not initial load), start fade out
    if (previousPin && previousPin.id !== pin.id) {
      setIsTransitioning(true);
      
      // Reset scroll position when changing pins
      if (descriptionRef.current) {
        descriptionRef.current.scrollTop = 0;
      }
      
      // Wait for fade out, then load new content
      setTimeout(() => {
        fetchAndSetContent(pin);
      }, 300); // Wait for fade out to complete
    } else {
      // Initial load or same pin - load immediately
      fetchAndSetContent(pin);
    }

    setPreviousPin(pin);
  }, [pin]);

  const fetchAndSetContent = async (pin) => {
    try {
      const response = await fetch(`/content/${pin.markdownFile}`);
      if (!response.ok) {
        throw new Error('Failed to load content');
      }
      const markdownText = await response.text();
      
      // Parse metadata and get content without metadata section
      const { metadata, contentWithoutMetadata } = parseMetadata(markdownText);
      
      // Parse the content (without metadata) to extract title, first paragraph, and first image
      const lines = contentWithoutMetadata.split('\n');
      let title = '';
      let firstParagraph = '';
      let firstImage = null;
      
      // Extract title (first H1 after metadata)
      for (let line of lines) {
        if (line.startsWith('# ')) {
          title = line.substring(2).trim();
          break;
        }
      }
      
      // Extract first image
      const imageRegex = /!\[.*?\]\(([^"\s]+)(?:\s+"[^"]*")?\)/;
      const imageMatch = contentWithoutMetadata.match(imageRegex);
      if (imageMatch) {
        firstImage = imageMatch[1];
        console.log('Extracted image path:', firstImage); // Debug log
      }
      
      // Extract first paragraph (first non-empty line that's not a header or image)
      for (let line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine && 
            !trimmedLine.startsWith('#') && 
            !trimmedLine.startsWith('!') &&
            !trimmedLine.startsWith('![') &&
            trimmedLine.length > 20) { // Ensure it's a substantial paragraph
          firstParagraph = trimmedLine;
          break;
        }
      }
      
      setContent({
        title: metadata.title || title || pin.title,
        firstParagraph: metadata.description || firstParagraph || pin.description,
        firstImage,
        category: metadata.category || pin.category,
        date: metadata.date || '',
        loading: false,
        error: false
      });
      
      // Fade in new content
      setIsTransitioning(false);
    } catch (error) {
      setContent({
        title: pin.title,
        firstParagraph: pin.description,
        firstImage: null,
        category: pin.category,
        date: '',
        loading: false,
        error: true
      });
      
      // Fade in even on error
      setIsTransitioning(false);
    }
  };


  if (!pin) {
    return (
      <div className="side-panel">
        <div className={`side-panel-content ${isTransitioning ? 'fade-transition' : ''}`}>
          <div className="side-panel-placeholder">
            <h3>Explore Montreal's Music Scene</h3>
            <p>Hover over any marker to learn more about Montreal's vibrant music culture, venues, artists, and events.</p>
            <div className="side-panel-logo">
              <img src="/logo_v2_nav.png" alt="MHM Logo" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="side-panel">
      <div className={`side-panel-content ${isTransitioning ? 'fade-transition' : ''}`}>
        {content.loading ? (
          <div className="side-panel-loading">
            <h3>{pin.title}</h3>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Image thumbnail */}
            {content.firstImage && (
              <div className="side-panel-image">
                <img src={content.firstImage} alt={content.title} />
              </div>
            )}
            
            {/* Content */}
            <div className="side-panel-text">
              <h3 className="side-panel-title">{content.title}</h3>
              {content.date && <div className="side-panel-date">{content.date}</div>}
              <div className="side-panel-category">{content.category}</div>
              <p className="side-panel-description" ref={descriptionRef}>
                {content.firstParagraph}
              </p>
              <button className="side-panel-button" onClick={(e) => {
                e.stopPropagation();
                onNavigate(pin.id);
              }}>
                Explore
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SidePanel;